import styled from 'styled-components';
import { Tier } from 'dragonmouse-shared/interfaces';

export const Base = styled.div<{ isCurrent?: boolean; tier?: Tier.TIER_1 | Tier.TIER_2 }>`
	background: ${(p) => p.theme.gray[0]};
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
	transition: all 0.3s ease-in-out;
	display: flex;
	flex-direction: column;
	text-align: left;
	position: relative;
	border-left: ${(p) => (p.isCurrent ? '4px solid #1E90FF' : 'none')};
	border-top: ${(p) => (p.tier === Tier.TIER_2 ? '2px solid #8B5CF6' : 'none')};
	background: ${(p) => (p.isCurrent ? 'rgba(30, 144, 255, 0.05)' : p.theme.gray[0])};

	&:hover {
		transform: translateY(-4px);
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
	}
`;

export const CurrentPlanBadge = styled.span`
	position: absolute;
	top: 16px;
	right: 16px;
	background: linear-gradient(135deg, #1e90ff 0%, #4169e1 100%);
	color: ${(p) => p.theme.white};
	padding: 6px 14px;
	border-radius: 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

export const Name = styled.h2<{ tier?: Tier.TIER_1 | Tier.TIER_2 }>`
	font-size: 24px;
	font-weight: 700;
	margin-bottom: ${(p) => (p.tier === Tier.TIER_2 ? '8px' : '16px')};
	color: ${(p) => {
		if (p.tier === Tier.TIER_1) return '#1E90FF';
		if (p.tier === Tier.TIER_2) return '#8B5CF6';
		return p.theme.gray[1000];
	}};
`;

export const UpgradeHeadline = styled.h3`
	font-size: 20px;
	font-weight: 700;
	margin-bottom: 8px;
	color: #8b5cf6;
`;

export const UpgradeDescription = styled.p`
	font-size: 14px;
	color: ${(p) => p.theme.gray[600]};
	margin-bottom: 16px;
	line-height: 1.5;
`;

export const RenewalInfo = styled.div`
	font-size: 13px;
	color: ${(p) => p.theme.gray[600]};
	margin-bottom: 20px;
`;

export const Pricing = styled.div`
	margin-bottom: 24px;
`;

export const Price = styled.div`
	font-size: 32px;
	font-weight: 600;
	line-height: 40px;
	color: ${(p) => p.theme.gray[1000]};

	@media (max-width: 480px) {
		font-size: 28px;
	}
`;

export const PricePeriod = styled.span`
	font-size: 16px;
	color: ${(p) => p.theme.gray[600]};
	font-weight: 400;
`;

export const PriceDifference = styled.div`
	display: inline-block;
	margin-top: 8px;
	font-size: 18px;
	font-weight: 600;
	color: #8b5cf6;
`;

export const SavingsBadge = styled.span<{ tier?: Tier.TIER_1 | Tier.TIER_2 }>`
	display: inline-block;
	margin-top: 8px;
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	background: ${(p) =>
		p.tier === Tier.TIER_1 ? 'rgba(30, 144, 255, 0.2)' : 'rgba(139, 92, 246, 0.2)'};
	color: ${(p) => (p.tier === Tier.TIER_1 ? '#1E90FF' : '#8B5CF6')};
`;

export const Features = styled.ul`
	list-style: none;
	margin-bottom: 24px;
	flex-grow: 1;
	padding: 0;
`;

export const Feature = styled.li<{ tier?: Tier.TIER_1 | Tier.TIER_2 }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 0;
	font-size: 15px;
	line-height: 22px;
	font-weight: 500;
	color: ${(p) => p.theme.gray[1000]};
`;

export const Checkmark = styled.svg<{ tier?: Tier.TIER_1 | Tier.TIER_2 }>`
	width: 20px;
	height: 20px;
	flex-shrink: 0;
	color: ${(p) => {
		if (p.tier === Tier.TIER_1) return '#1E90FF';
		if (p.tier === Tier.TIER_2) return '#8B5CF6';
		return p.theme.gray[600];
	}};
`;

export const CTAButton = styled.div<{ variant?: 'manage' | 'pro' | 'max' }>`
	width: 100%;
	height: 48px;
	border: none;
	border-radius: 8px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease-in-out;
	color: ${(p) => p.theme.white};
	font-family: inherit;
	display: flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;

	${(p) => {
		if (p.variant === 'manage') {
			return `
				background: ${p.theme.gray[200]};
				color: ${p.theme.gray[1000]};
				&:hover {
					background: ${p.theme.gray[400]};
					transform: scale(1.02);
				}
			`;
		}
		if (p.variant === 'pro') {
			return `
				background: linear-gradient(135deg, #1E90FF 0%, #4169E1 100%);
				&:hover {
					background: linear-gradient(135deg, #2EA0FF 0%, #5179F1 100%);
					transform: scale(1.02);
				}
			`;
		}
		if (p.variant === 'max') {
			return `
				background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
				&:hover {
					background: linear-gradient(135deg, #9B6CF6 0%, #8C4EED 100%);
					transform: scale(1.02);
				}
			`;
		}
		return '';
	}}
`;

export const CancelLink = styled.a`
	display: block;
	text-align: center;
	margin-top: 12px;
	color: ${(p) => p.theme.gray[600]};
	font-size: 13px;
	font-weight: 500;
	text-decoration: none;
	transition: color 0.2s ease-in-out;
	cursor: pointer;

	&:hover {
		color: ${(p) => p.theme.gray[400]};
		text-decoration: none;
	}

	&:visited {
		text-decoration: none;
	}
`;

export const LinkWrapper = styled.a`
	text-decoration: none;

	&:hover {
		text-decoration: none;
	}

	&:visited {
		text-decoration: none;
	}
`;
